import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  clearRefreshToken,
  getRefreshToken,
  storeRefreshToken,
} from '../lib/tokenStore';
import { ACCESS_TOKEN_KEY } from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  companyId: number | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (returnTo?: string) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface MeResponse {
  authenticated: boolean;
  user?: User;
  tokenExpiresAt?: string;
}

interface RefreshResponse {
  access_token?: string;
  refresh_token?: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const verifyAndSetUser = useCallback(
    async (accessToken: string): Promise<boolean> => {
      try {
        const res = await fetch('/auth/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return false;
        const data = (await res.json()) as MeResponse;
        if (data.authenticated && data.user) {
          setUser(data.user);
          setToken(accessToken);
          sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
          return true;
        }
      } catch {
        // fall through
      }
      return false;
    },
    [],
  );

  /**
   * Silent refresh via IndexedDB refresh token. On success, stores the rotated
   * refresh token AND the new access token, and returns the access token.
   */
  const attemptSilentRefresh = useCallback(async (): Promise<string | null> => {
    const refreshToken = await getRefreshToken();
    if (!refreshToken) return null;

    try {
      const res = await fetch('/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!res.ok) {
        await clearRefreshToken();
        return null;
      }

      const data = (await res.json()) as RefreshResponse;
      if (data.refresh_token) {
        await storeRefreshToken(data.refresh_token);
      }
      return data.access_token ?? null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function boot(): Promise<void> {
      // 1. Callback landing: parse tokens from the URL hash.
      const hash = window.location.hash;
      if (hash.includes('auth-complete')) {
        const params = new URLSearchParams(hash.split('?')[1] ?? '');
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const returnTo = params.get('return_to') || '/';

        if (accessToken && refreshToken) {
          await storeRefreshToken(refreshToken);
          sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
          window.history.replaceState(null, '', returnTo);

          if (!cancelled && (await verifyAndSetUser(accessToken))) {
            if (!cancelled) setIsLoading(false);
            return;
          }
        }
      }

      // 2. Existing access token in sessionStorage.
      const storedAccess = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      if (storedAccess) {
        if (!cancelled && (await verifyAndSetUser(storedAccess))) {
          if (!cancelled) setIsLoading(false);
          return;
        }
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      }

      // 3. Silent refresh from IndexedDB refresh token.
      const freshAccess = await attemptSilentRefresh();
      if (freshAccess && !cancelled) {
        await verifyAndSetUser(freshAccess);
        if (!cancelled) setIsLoading(false);
        return;
      }

      // 4. Nothing worked — user needs to login.
      if (!cancelled) setIsLoading(false);
    }

    void boot();
    return () => {
      cancelled = true;
    };
  }, [attemptSilentRefresh, verifyAndSetUser]);

  const login = useCallback((returnTo?: string) => {
    const params = returnTo
      ? `?return_to=${encodeURIComponent(returnTo)}`
      : '';
    window.location.href = `/auth/login${params}`;
  }, []);

  const logout = useCallback(async () => {
    // sendBeacon guarantees delivery even if the page navigates away.
    const refreshToken = await getRefreshToken();
    if (refreshToken) {
      const payload = JSON.stringify({ refresh_token: refreshToken });
      const blob = new Blob([payload], { type: 'application/json' });
      const sent = navigator.sendBeacon('/auth/revoke', blob);
      if (!sent) {
        try {
          await Promise.race([
            fetch('/auth/revoke', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: payload,
            }),
            new Promise((_resolve, reject) =>
              setTimeout(() => reject(new Error('timeout')), 3000),
            ),
          ]);
        } catch {
          // Best-effort — token will eventually expire.
        }
      }
    }

    await clearRefreshToken();
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    setUser(null);
    setToken(null);
    window.location.href = '/auth/logout';
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      token,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
    }),
    [user, token, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
